import React, { useEffect, useState } from "react";
import {
  FaPhone,
  FaWhatsapp,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaMapMarkerAlt,
} from "react-icons/fa";

const formatPhoneNumber = (number) => {
  if (!number) return "";

  const digits = number.replace(/\D/g, "");

  if (digits.length === 10) {
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  }

  return number;
};

const formatWhatsAppNumber = (number) => {
  if (!number) return "";

  const digits = number.replace(/\D/g, "");

  // Convert South African number from 0XXXXXXXXX
  // to international format 27XXXXXXXXX
  if (digits.startsWith("0") && digits.length === 10) {
    return `27${digits.slice(1)}`;
  }

  return digits;
};

const ProductDetailsModal = ({ product, onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Reset to the first image whenever a different product is opened
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [product?.id]);

  // Product may not exist
  if (!product) return null;

  // Build an array containing only images that actually exist
  const images = [
    product.image1,
    product.image2,
    product.image3,
    product.image4,
    product.image5,
  ].filter(Boolean);

  const hasPreviousImage = currentImageIndex > 0;
  const hasNextImage = currentImageIndex < images.length - 1;

  const goToPreviousImage = () => {
    if (hasPreviousImage) {
      setCurrentImageIndex((prev) => prev - 1);
    }
  };

  const goToNextImage = () => {
    if (hasNextImage) {
      setCurrentImageIndex((prev) => prev + 1);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">

      {/* MODAL */}
      <div className="relative bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl shadow-2xl">

        {/* CLOSE BUTTON */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-20 bg-red-600 hover:bg-red-700 text-white rounded-full w-9 h-9 flex items-center justify-center transition"
          aria-label="Close"
        >
          <FaTimes />
        </button>

        {/* IMAGE */}
        <div className="relative w-full aspect-square bg-gray-100 overflow-hidden rounded-t-xl">

          <img
            src={images[currentImageIndex]}
            alt={`${product.name} - Image ${currentImageIndex + 1}`}
            className="w-full h-full object-cover"
          />

          {/* PREVIOUS BUTTON */}
          {hasPreviousImage && (
            <button
              onClick={goToPreviousImage}
              className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white rounded-full w-10 h-10 flex items-center justify-center transition shadow-lg"
              aria-label="Previous image"
            >
              <FaChevronLeft />
            </button>
          )}

          {/* NEXT BUTTON */}
          {hasNextImage && (
            <button
              onClick={goToNextImage}
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white rounded-full w-10 h-10 flex items-center justify-center transition shadow-lg"
              aria-label="Next image"
            >
              <FaChevronRight />
            </button>
          )}

          {/* IMAGE COUNTER */}
          {images.length > 1 && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1 rounded-full">
              {currentImageIndex + 1} / {images.length}
            </div>
          )}

        </div>

        {/* DETAILS */}
        <div className="p-5">

          {/* NAME */}
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {product.name}
          </h2>

          {/* PRICE */}
          <p className="text-green-800 font-bold text-xl mb-3">
            R{Number(product.price).toLocaleString("en-ZA", {
              maximumFractionDigits: 0,
            })}
          </p>

          {/* LOCATION */}
          {product.location && (
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
              <FaMapMarkerAlt className="text-green-700 flex-shrink-0" />
              <span>{product.location}</span>
            </div>
          )}

          {/* DESCRIPTION */}
          {product.description && (
            <div className="mb-5">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Description
              </h3>

              <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                {product.description}
              </p>
            </div>
          )}

          {/* CONTACT DETAILS */}
          {(product.contact_phone || product.contact_whatsapp) && (
            <div className="border-t border-gray-200 pt-4">

              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Contact Seller
              </h3>

              <div className="space-y-2">

                {/* PHONE */}
                {product.contact_phone && (
                  <a
                    href={`tel:${product.contact_phone}`}
                    className="flex items-center gap-3 text-gray-700 hover:text-green-800 transition"
                  >
                    <FaPhone className="text-green-800 flex-shrink-0" />

                    <span>
                      {formatPhoneNumber(product.contact_phone)}
                    </span>
                  </a>
                )}

                {/* WHATSAPP */}
                {product.contact_whatsapp && (
                  <a
                    href={`https://wa.me/${formatWhatsAppNumber(
                      product.contact_whatsapp
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-gray-700 hover:text-green-800 transition"
                  >
                    <FaWhatsapp className="text-green-700 flex-shrink-0" />

                    <span>
                      {formatPhoneNumber(product.contact_whatsapp)}
                    </span>
                  </a>
                )}

              </div>

            </div>
          )}

        </div>

      </div>
    </div>
  );
};

export default ProductDetailsModal;